import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import { Calculator, Save, Trash2, TrendingUp, Receipt, Wallet, ChevronRight } from 'lucide-react';
import SearchBar, { matches } from '../components/app/SearchBar';

const PRESET_MODALITIES = [
  { paid: 10, contract: 15 },
  { paid: 15, contract: 20 },
  { paid: 20, contract: 25 },
  { paid: 5, contract: 10 },
  { paid: 8, contract: 12 },
];

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

const Capitalizaciones = () => {
  const [form, setForm] = useState({ monthly_rent: 800, years_paid: 10, years_contract: 15, payment_split: 1, custom_tax_pct: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');

  const calc = async () => {
    setLoading(true);
    try {
      const payload = {
        monthly_rent: parseFloat(form.monthly_rent) || 0,
        years_paid: parseInt(form.years_paid) || 0,
        years_contract: parseInt(form.years_contract) || 0,
        payment_split: parseInt(form.payment_split) || 1,
        custom_tax_pct: form.custom_tax_pct === '' ? null : parseFloat(form.custom_tax_pct),
      };
      const r = await api.post('/capitalizations/calculate', payload);
      setResult(r.data);
    } catch (e) { alert('Error: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { calc(); }, []); // initial calc

  const loadSaved = async () => {
    const r = await api.get('/capitalizations');
    setSaved(r.data);
  };
  const loadSites = async () => {
    const r = await api.get('/sites/lite');
    setSiteList(r.data);
  };
  useEffect(() => { loadSaved(); loadSites(); }, []);

  const save = async () => {
    try {
      await api.post('/capitalizations', {
        monthly_rent: parseFloat(form.monthly_rent) || 0,
        years_paid: parseInt(form.years_paid) || 0,
        years_contract: parseInt(form.years_contract) || 0,
        payment_split: parseInt(form.payment_split) || 1,
        custom_tax_pct: form.custom_tax_pct === '' ? null : parseFloat(form.custom_tax_pct),
        site_id: siteId || null,
        owner_name: ownerName,
        notes,
      });
      await loadSaved();
      setOwnerName(''); setNotes('');
      alert('Guardado correctamente');
    } catch (e) { alert('Error: ' + (e?.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!confirm('¿Eliminar?')) return;
    await api.delete(`/capitalizations/${id}`);
    loadSaved();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">CAPITALIZACIONES</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Calculadora fiscal</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Calcula importes brutos, retención IRPF (rentas del ahorro 2025) y neto por pago.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        {/* Form */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 h-fit">
          <div className="flex items-center gap-2 mb-5">
            <Calculator className="text-[#A3FF12]" size={20}/>
            <h2 className="text-xl font-black text-white">Parámetros</h2>
          </div>

          <div className="text-white/60 text-xs font-bold mb-2">MODALIDADES RÁPIDAS</div>
          <div className="flex flex-wrap gap-2 mb-5">
            {PRESET_MODALITIES.map((m) => (
              <button key={`${m.paid}x${m.contract}`}
                onClick={() => setForm({ ...form, years_paid: m.paid, years_contract: m.contract })}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  form.years_paid === m.paid && form.years_contract === m.contract
                    ? 'bg-[#A3FF12] text-black border-[#A3FF12]'
                    : 'border-white/10 text-white/70 hover:border-[#A3FF12]/40'
                }`}>{m.paid}x{m.contract}</button>
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-white/80 text-sm font-semibold">Renta mensual (€)</label>
              <input type="number" step="0.01" value={form.monthly_rent} onChange={(e) => setForm({...form, monthly_rent: e.target.value})}
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/80 text-sm font-semibold">Años pagados</label>
                <input type="number" value={form.years_paid} onChange={(e) => setForm({...form, years_paid: e.target.value})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
              </div>
              <div>
                <label className="text-white/80 text-sm font-semibold">Años contrato</label>
                <input type="number" value={form.years_contract} onChange={(e) => setForm({...form, years_contract: e.target.value})}
                  className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
              </div>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">Fraccionamiento de pago</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[1,2,3].map((s) => (
                  <button key={s} onClick={() => setForm({...form, payment_split: s})}
                    className={`py-3 rounded-xl text-sm font-bold border transition-colors ${
                      form.payment_split === s ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'
                    }`}>{s} pago{s>1?'s':''}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-white/80 text-sm font-semibold">% impuesto personalizado <span className="text-white/40 text-xs">(vacío = IRPF tramos)</span></label>
              <input type="number" step="0.1" value={form.custom_tax_pct} onChange={(e) => setForm({...form, custom_tax_pct: e.target.value})} placeholder="Ej. 19"
                className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-[#A3FF12]"/>
            </div>
            <button onClick={calc} disabled={loading}
              className="w-full bg-[#A3FF12] text-black font-black py-3 rounded-xl hover:bg-[#b8ff3a] transition-colors disabled:opacity-60">
              Calcular
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="text-white/60 text-xs font-bold mb-3">GUARDAR EN BBDD</div>
            <div className="space-y-3">
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)}
                className="w-full bg-black border border-white/15 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#A3FF12]">
                <option value="">— Sin emplazamiento —</option>
                {siteList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Nombre propietario / CCPP"
                className="w-full bg-black border border-white/15 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#A3FF12]"/>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas"
                className="w-full bg-black border border-white/15 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#A3FF12]"/>
              <button onClick={save} className="w-full inline-flex items-center justify-center gap-2 border border-white/15 text-white py-2.5 rounded-xl font-bold hover:bg-white/5 hover:border-[#A3FF12]/40 transition-colors">
                <Save size={16}/> Guardar cálculo
              </button>
            </div>
          </div>
        </div>

        {/* Result */}
        <div>
          {result && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-[#0a0a0a] to-[#0a0a0a] border border-[#A3FF12]/30 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[#A3FF12] text-xs font-black tracking-widest">MODALIDAD {result.modality}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div>
                    <div className="text-white/50 text-xs">Importe bruto total</div>
                    <div className="text-2xl font-black text-white">{eur(result.gross_total)}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">Impuestos totales</div>
                    <div className="text-2xl font-black text-red-400">{eur(result.total_tax)}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">Neto total</div>
                    <div className="text-2xl font-black text-[#A3FF12]">{eur(result.total_net)}</div>
                  </div>
                  <div>
                    <div className="text-white/50 text-xs">Tipo efectivo</div>
                    <div className="text-2xl font-black text-white">{result.effective_tax_pct}%</div>
                  </div>
                </div>
                <div className="text-white/40 text-xs mt-3">Renta mensual {eur(result.monthly_rent)} · {result.years_paid} años pagados · Contrato {result.years_contract} años · {result.payment_split} pago(s)</div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {result.tax_per_payment.map((tax, idx) => (
                  <div key={idx} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div className="text-white/60 text-xs font-black tracking-widest">PAGO {idx + 1}</div>
                      <Receipt className="text-[#A3FF12]" size={16}/>
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-white/50">Bruto</span><span className="text-white font-bold">{eur(result.per_payment_gross)}</span></div>
                      <div className="flex justify-between"><span className="text-white/50">Impuesto</span><span className="text-red-400 font-bold">-{eur(tax)}</span></div>
                      <div className="flex justify-between pt-2 border-t border-white/10"><span className="text-white">Neto</span><span className="text-[#A3FF12] font-black">{eur(result.net_per_payment[idx])}</span></div>
                    </div>
                    {result.tax_brackets_per_payment[idx]?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                        <div className="text-white/40 text-xs uppercase">Tramos IRPF</div>
                        {result.tax_brackets_per_payment[idx].map((b, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-white/60">{b.pct}% sobre {eur(b.amount_in_bracket)}</span>
                            <span className="text-white/80">{eur(b.tax_in_bracket)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Saved history */}
          <div className="mt-8">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h3 className="text-white font-black text-xl">Histórico de cálculos guardados ({saved.length})</h3>
              {saved.length > 0 && (
                <div className="flex-1 min-w-[200px] max-w-md">
                  <SearchBar value={search} onChange={setSearch} placeholder="Buscar por propietario, notas o modalidad…" testId="cap-search"/>
                </div>
              )}
            </div>
            {saved.length === 0 ? (
              <p className="text-white/40 text-sm bg-[#0a0a0a] border border-white/10 rounded-xl p-6 text-center">Sin cálculos guardados todavía.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                {saved.filter((c) => matches(search, c.owner_name, c.notes, c.result?.modality)).map((c) => (
                  <div key={c.id} className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white font-bold truncate">{c.owner_name || c.result.modality}</div>
                      <div className="text-white/40 text-xs truncate">
                        {c.result.modality} · {eur(c.result.gross_total)} bruto · {eur(c.result.total_net)} neto · {c.payment_split} pago(s)
                      </div>
                      {c.notes && <div className="text-white/30 text-xs truncate mt-1">{c.notes}</div>}
                    </div>
                    <button onClick={() => remove(c.id)} className="text-white/40 hover:text-red-400"><Trash2 size={16}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Capitalizaciones;
